import {Box, Col, css, DSButton, Row} from "@cdx/ds";
import {ReactElement, ReactNode, useState} from "react";
import {MaybeInaccessibleChildCardLane as RawMaybeInaccessibleChildCardLane} from "./ChildCardLane";
import {TooltipForChild} from "@cdx/common";
import {api} from "../../lib/api";
import RawCardPicker from "../../components/CardPicker";
import getSelectedProjects from "../../lib/hooks/useSelectedProjects";

const MaybeInaccessibleChildCardLane = RawMaybeInaccessibleChildCardLane as any;

const CardPicker = RawCardPicker as any;

export const AttachToParentForCardCreation = (props: {
  root: any;
  card: any;
  onSelect: (id: string) => void;
}) => {
  const {root, card, onSelect} = props;

  const selectedProjects = getSelectedProjects(root);
  const filterProjectIds = new Set(
    [
      card.deck?.project.$meta.get("id", null),
      ...selectedProjects.map((p: any) => p.$meta.get("id", null)),
    ].filter(Boolean)
  );
  const validParentCardFilter = {
    deck: {projectId: [...filterProjectIds]},
    parentCardId: null,
    visibility: "default",
    ...(!card.isDoc && {isDoc: [null, false]}),
  };

  return (
    <CardPicker
      onSelect={onSelect}
      root={root}
      filter={validParentCardFilter}
      emptyMessage="The search exludes private and sub cards as those are no valid Hero cards."
      label="Enter title of Hero card"
    />
  );
};

const WarnBox = ({children}: {children: ReactNode}) => (
  <Box
    colorTheme="warn25"
    bg="foreground"
    color="primary"
    bold
    pa="12px"
    size={14}
    borderColor="default"
    borderWidth={1}
    rounded={4}
  >
    {children}
  </Box>
);

const Validate = (props: {card: any; children: ReactNode}) => {
  const {card, children} = props;
  if (!card.deck) {
    return <WarnBox>You can't turn a private card into a sub card.</WarnBox>;
  }
  return children as ReactElement;
};

const SubCardHeroActions = (props: {
  onClick: () => void;
  active: boolean;
  onItemRemove: (id: string) => any;
  card: any;
}) => {
  const {onItemRemove, onClick, active, card} = props;
  return (
    <Row ml="auto" sp="8px">
      <DSButton variant="tertiary" size="sm" negatePadding onClick={onClick} active={active}>
        Change
      </DSButton>
      <TooltipForChild tooltip="Remove opened card from this Hero card">
        <DSButton
          variant="tertiary"
          size="sm"
          negatePadding
          onClick={() => onItemRemove(card.cardId)}
        >
          Remove
        </DSButton>
      </TooltipForChild>
    </Row>
  );
};

type Mode = "default" | "selectParent";

type ContentProps = {
  card: any;
  root: any;
  onChange: (arg: any) => void;
};
const Content = (props: ContentProps) => {
  const {card, root, onChange} = props;
  const [mode, setMode] = useState<Mode>(card.parentCardId ? "default" : "selectParent");
  const toggleMode = (target: Mode) => setMode((v) => (v === target ? "default" : target));

  const handleItemRemove = () => onChange({parentCardId: null});

  return (
    <Col sp="32px">
      <Col sp="12px">
        <Row sp="12px">
          <Box as="h2" size={16}>
            Hero Card
          </Box>
          {card.parentCardId && (
            <SubCardHeroActions
              card={card}
              onItemRemove={handleItemRemove}
              onClick={() => toggleMode("selectParent")}
              active={mode === "selectParent"}
            />
          )}
        </Row>
        {card.parentCardId ? (
          <MaybeInaccessibleChildCardLane
            root={root}
            card={api.getModel({id: card.parentCardId, modelName: "card"} as any)}
            interactable={false}
          />
        ) : (
          <DSButton
            variant="secondary"
            onClick={() => toggleMode("selectParent")}
            active={mode === "selectParent"}
            className={css({alignSelf: "start"})}
          >
            Select
          </DSButton>
        )}
        {mode === "selectParent" && (
          <AttachToParentForCardCreation
            root={root}
            card={card}
            onSelect={(id) => {
              onChange({parentCardId: id});
              setMode("default");
            }}
          />
        )}
      </Col>
    </Col>
  );
};

const WorkflowSidebarForCardCreation = ({
  card,
  root,
  onChange,
}: {
  card: any;
  root: any;
  onChange: (arg: any) => void;
}) => {
  return (
    <Col px="12px" pt="12px" pb="32px" sp="12px" data-cdx-context="WorkflowSidebar">
      <Validate card={card}>
        <Content card={card} root={root} onChange={onChange} />
      </Validate>
    </Col>
  );
};

export default WorkflowSidebarForCardCreation;
